import { useEffect, useMemo, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { Modal } from '../ui/Modal'
import { EmailPreview } from './EmailPreview'
import type { ReportFormat, SavedReport, Schedule, ScheduleDraft } from './reportsStore'
import { defaultSubject, fillSubject, formatRun, isEmail, nextRuns, periodLabel, splitEmails, summary, TIMEZONE, WEEK_ORDER, WEEKDAYS, type Frequency } from './schedule'
import type { Locale } from './types'

/**
 * PROGRAMAREA UNUI RAPORT PE EMAIL: cand pleaca, la cine, in ce limba si ce
 * fisiere, plus cum va arata emailul. Se salveaza in baza; trimite cronul din
 * ph4. Previzualizarea (`EmailPreview`) e chiar emailul, desenat de server cu
 * acelasi `ReportEmail`. "Ruleaza acum" sta sub raport, langa istoricul
 * rularilor.
 */

const FORMAT_LABELS: Record<ReportFormat, string> = { pdf: 'PDF', xlsx: 'Excel (.xlsx)', csv: 'CSV (.csv)' }

/** Cate randuri din fiecare tabel intra in corpul emailului; ca in cronul din ph4 (`ReportEmail`). */
const BODY_ROWS_DEFAULT = 10
const BODY_ROWS_CHOICES = [0, 5, 10, 25, 50]

type Form = Omit<ScheduleDraft, 'report_id'>

function initialForm(report: SavedReport, schedule: Schedule | null, locale: Locale): Form {
  if (schedule) {
    const { id: _id, report_id: _r, next_run_at: _n, last_run_at: _l, ...rest } = schedule
    void _id
    void _r
    void _n
    void _l
    return rest
  }
  return {
    enabled: true,
    frequency: 'weekly',
    weekday: 1,
    month_day: 1,
    time: '08:00',
    timezone: TIMEZONE,
    recipients: [],
    locale,
    formats: ['pdf'],
    subject: defaultSubject(report.name),
    message: '',
    body_rows: BODY_ROWS_DEFAULT,
  }
}

export function ScheduleDialog({ open, report, schedule, locale, error = '', now = () => new Date(), onClose, onSave }: {
  open: boolean
  report: SavedReport
  schedule: Schedule | null
  locale: Locale
  /** Refuzul serverului, tradus. */
  error?: string
  now?: () => Date
  onClose(): void
  onSave(draft: ScheduleDraft): void
}) {
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const [form, setForm] = useState<Form>(() => initialForm(report, schedule, locale))
  const [typing, setTyping] = useState('')
  const [tried, setTried] = useState(false)

  useEffect(() => {
    if (open) {
      setForm(initialForm(report, schedule, locale))
      setTyping('')
      setTried(false)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }))
  const runs = useMemo(() => nextRuns(form, now(), 3), [form.frequency, form.weekday, form.month_day, form.time, form.timezone]) // oxlint-disable-line react-hooks/exhaustive-deps

  /* Ce s-a scris in casuta intra in lista la virgula, Enter, iesire sau lipire. */
  const commit = (text: string) => {
    const add = splitEmails(text, form.recipients)
    if (add.length > 0) set('recipients', [...form.recipients, ...add])
    setTyping('')
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
      if (typing.trim() === '') return
      e.preventDefault()
      commit(typing)
    } else if (e.key === 'Backspace' && typing === '' && form.recipients.length > 0) {
      set('recipients', form.recipients.slice(0, -1))
    }
  }
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    if (!/[,;\s]/.test(text.trim())) return
    e.preventDefault()
    commit(typing + text)
  }

  const invalid = form.recipients.filter((r) => !isEmail(r))
  const pending = typing.trim() !== ''
  const errors: string[] = []
  if (form.recipients.length === 0 && !pending) errors.push(t('Adauga cel putin o adresa.', 'Add at least one recipient.'))
  if (invalid.length > 0) errors.push(t(`Adrese gresite: ${invalid.join(', ')}.`, `Invalid addresses: ${invalid.join(', ')}.`))
  if (form.formats.length === 0) errors.push(t('Alege cel putin un format.', 'Pick at least one format.'))
  if (form.subject.trim() === '') errors.push(t('Subiectul nu poate fi gol.', 'The subject cannot be empty.'))

  const save = () => {
    setTried(true)
    /* Ce a ramas scris in casuta se ia si el, ca la iesirea din ea. */
    const extra = pending ? splitEmails(typing, form.recipients) : []
    const recipients = [...form.recipients, ...extra]
    if (pending) {
      set('recipients', recipients)
      setTyping('')
    }
    const bad = recipients.filter((r) => !isEmail(r))
    if (recipients.length === 0 || bad.length > 0 || form.formats.length === 0 || form.subject.trim() === '') return
    onSave({ ...form, recipients, subject: form.subject.trim(), message: form.message.trim(), report_id: report.id, id: schedule?.id })
  }

  const firstRun = runs[0] ?? now()
  const period = periodLabel(form.frequency, firstRun, form.locale)
  const toggleFormat = (f: ReportFormat) => set('formats', form.formats.includes(f) ? form.formats.filter((x) => x !== f) : [...form.formats, f])

  return (
    <Modal
      id="od-rep-schedule"
      labelledBy="od-rep-schedule-title"
      open={open}
      onClose={onClose}
      portal
      large
      title={
        <>
          {t('Programeaza pe email', 'Schedule by email')}: <span className="od-rep-modal-sub">{report.name}</span>
        </>
      }
      footer={
        <div className="od-rep-sched-foot">
          <span className="od-rep-sched-foot-right">
            <button type="button" className="btn btn-default" onClick={onClose}>
              {t('Renunta', 'Cancel')}
            </button>
            <button type="button" className="btn btn-primary od-rep-sched-save" onClick={save}>
              {t('Salveaza programarea', 'Save schedule')}
            </button>
          </span>
        </div>
      }
    >
      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}
      <div className="od-rep-sched">
        <div className="od-rep-sched-form">
          <label className="od-rep-switch">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            <span className="od-rep-switch-track" aria-hidden="true" />
            <span>{form.enabled ? t('Programare activa', 'Schedule on') : t('Programare oprita', 'Schedule paused')}</span>
          </label>

          <div className="od-rep-form-row">
            <span className="od-rep-form-label">{t('Cat de des', 'How often')}</span>
            <div className="od-rep-seg" role="radiogroup" aria-label={t('Cat de des', 'How often')}>
              {(['daily', 'weekly', 'monthly'] as Frequency[]).map((f) => (
                <button key={f} type="button" role="radio" aria-checked={form.frequency === f} className={`od-rep-seg-btn${form.frequency === f ? ' active' : ''}`} onClick={() => set('frequency', f)}>
                  {f === 'daily' ? t('Zilnic', 'Daily') : f === 'weekly' ? t('Saptamanal', 'Weekly') : t('Lunar', 'Monthly')}
                </button>
              ))}
            </div>
          </div>

          <div className="od-rep-inline-row">
            {form.frequency === 'weekly' ? (
              <label className="od-rep-field">
                {t('Ziua', 'Day')}
                <select className="form-control input-sm" value={form.weekday} onChange={(e) => set('weekday', Number(e.target.value))} aria-label={t('Ziua saptamanii', 'Day of week')}>
                  {WEEK_ORDER.map((d) => (
                    <option key={d} value={d}>
                      {locale === 'ro' ? WEEKDAYS.ro[d]!.charAt(0).toUpperCase() + WEEKDAYS.ro[d]!.slice(1) : WEEKDAYS.en[d]}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {form.frequency === 'monthly' ? (
              <label className="od-rep-field">
                {t('Ziua lunii', 'Day of month')}
                <select className="form-control input-sm" value={form.month_day} onChange={(e) => set('month_day', Number(e.target.value))} aria-label={t('Ziua lunii', 'Day of month')}>
                  {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                  <option value={31}>{t('Ultima zi', 'Last day')}</option>
                </select>
              </label>
            ) : null}
            <label className="od-rep-field">
              {t('Ora (Bucuresti)', 'Time (Bucharest)')}
              <input type="time" className="form-control input-sm od-rep-time" value={form.time} step={300} onChange={(e) => set('time', e.target.value || '08:00')} aria-label={t('Ora', 'Time')} />
            </label>
          </div>

          <div className="od-rep-sched-when">
            <b className="od-rep-sched-sentence">{summary(form, locale)}</b>
            {form.enabled ? (
              <>
                <span className="od-rep-meta">{t('Urmatoarele trimiteri:', 'Next sends:')}</span>
                <ul className="od-rep-runs">
                  {runs.map((r) => (
                    <li key={r.getTime()}>{formatRun(r, locale)}</li>
                  ))}
                </ul>
              </>
            ) : (
              <span className="od-rep-meta">{t('Oprita: nu pleaca nimic pana o pornesti.', 'Paused: nothing goes out until you turn it on.')}</span>
            )}
          </div>

          <div className="od-rep-form-row">
            <span className="od-rep-form-label">{t('Destinatari', 'Recipients')}</span>
            <div className={`od-rep-emails form-control${tried && (invalid.length > 0 || form.recipients.length === 0) ? ' od-rep-invalid' : ''}`}>
              {form.recipients.map((r) => (
                <span key={r} className={`od-rep-email${isEmail(r) ? '' : ' od-rep-email-bad'}`} title={isEmail(r) ? r : t('Adresa nu e valida', 'Not a valid address')}>
                  {r}
                  <button type="button" className="od-rep-x" aria-label={t(`Scoate ${r}`, `Remove ${r}`)} onClick={() => set('recipients', form.recipients.filter((x) => x !== r))}>
                    ×
                  </button>
                </span>
              ))}
              <input
                className="od-rep-email-input"
                value={typing}
                placeholder={form.recipients.length === 0 ? 'name@company.com' : ''}
                aria-label={t('Adauga destinatari', 'Add recipients')}
                onChange={(e) => {
                  const v = e.target.value
                  if (/[,;]/.test(v)) commit(v)
                  else setTyping(v)
                }}
                onKeyDown={onKey}
                onPaste={onPaste}
                onBlur={() => typing.trim() !== '' && commit(typing)}
              />
            </div>
            <span className="od-rep-meta">
              {t('Orice adresa; poti lipi mai multe, despartite prin virgula sau punct si virgula.', 'Any address; you can paste several, separated by commas or semicolons.')}
              {form.recipients.length > 0 ? ` ${t(`${form.recipients.length} destinatari.`, `${form.recipients.length} recipient${form.recipients.length === 1 ? '' : 's'}.`)}` : ''}
            </span>
          </div>

          <div className="od-rep-inline-row">
            <div className="od-rep-form-row">
              <span className="od-rep-form-label">{t('Limba emailului', 'Email language')}</span>
              <div className="btn-group btn-group-sm" role="group">
                {(['en', 'ro'] as Locale[]).map((l) => (
                  <button key={l} type="button" className={`btn btn-default${form.locale === l ? ' active' : ''}`} aria-pressed={form.locale === l} onClick={() => set('locale', l)}>
                    {l === 'en' ? 'English' : 'Romana'}
                  </button>
                ))}
              </div>
            </div>
            <div className="od-rep-form-row">
              <span className="od-rep-form-label">{t('Atasamente', 'Attachments')}</span>
              <div className="od-rep-checks">
                {(['pdf', 'xlsx', 'csv'] as ReportFormat[]).map((f) => (
                  <label key={f} className="od-rep-check">
                    <input type="checkbox" checked={form.formats.includes(f)} onChange={() => toggleFormat(f)} /> {FORMAT_LABELS[f]}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <label className="od-rep-form-row">
            <span className="od-rep-form-label">{t('Randuri in corpul emailului', 'Rows in the email body')}</span>
            <select className="form-control input-sm od-rep-body-rows" value={form.body_rows ?? BODY_ROWS_DEFAULT} onChange={(e) => set('body_rows', Number(e.target.value))}>
              {BODY_ROWS_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n === 0 ? t('Niciunul (doar cifrele)', 'None (figures only)') : t(`Primele ${n}`, `First ${n}`)}
                </option>
              ))}
            </select>
            <span className="od-rep-meta">{t('Din fiecare tabel; raportul intreg ramane in atasament.', 'From each table; the full report stays in the attachment.')}</span>
          </label>

          <label className="od-rep-form-row">
            <span className="od-rep-form-label">{t('Subiect', 'Subject')}</span>
            <input className={`form-control input-sm${tried && form.subject.trim() === '' ? ' od-rep-invalid' : ''}`} value={form.subject} maxLength={200} onChange={(e) => set('subject', e.target.value)} />
            <span className="od-rep-meta">{t('{period} devine perioada fiecarui raport.', '{period} becomes the period of each report.')}</span>
          </label>
          <label className="od-rep-form-row">
            <span className="od-rep-form-label">
              {t('Mesaj', 'Message')} <em className="od-rep-optional">{t('(optional)', '(optional)')}</em>
            </span>
            <textarea className="form-control input-sm" rows={3} maxLength={2000} value={form.message} onChange={(e) => set('message', e.target.value)} />
          </label>

          {tried && errors.length > 0 ? (
            <ul className="od-rep-form-errors" role="alert">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <EmailPreview
          open={open}
          report={report}
          locale={locale}
          recipients={form.recipients}
          fallbackSubject={fillSubject(form.subject, period)}
          request={{
            frequency: form.frequency,
            weekday: form.weekday,
            month_day: form.month_day,
            time: form.time,
            timezone: form.timezone,
            locale: form.locale,
            formats: form.formats,
            subject: form.subject,
            message: form.message,
            body_rows: form.body_rows ?? BODY_ROWS_DEFAULT,
          }}
        />
      </div>
    </Modal>
  )
}
